// IndexedDB 缓存层 - 用于 K 线历史数据
// 设计：每个 (code, interval) 一个 store，存 candles 数组 + lastFetchAt
import { openDB, type IDBPDatabase } from 'idb';
import type { Candle } from '../shared/types';

const DB_NAME = 'hei-ge-brothers-kline';
const DB_VERSION = 1;

interface KlineRecord {
  code: string;
  interval: string; // '1d' | '1h' 等
  candles: Candle[];
  lastFetchAt: number;
}

interface MetaRecord {
  key: string;
  value: any;
}

let dbPromise: Promise<IDBPDatabase> | null = null;

function getDB(): Promise<IDBPDatabase> {
  if (dbPromise) return dbPromise;
  dbPromise = openDB(DB_NAME, DB_VERSION, {
    upgrade(db) {
      if (!db.objectStoreNames.contains('klines')) {
        db.createObjectStore('klines', { keyPath: ['code', 'interval'] });
      }
      if (!db.objectStoreNames.contains('meta')) {
        db.createObjectStore('meta', { keyPath: 'key' });
      }
    },
  });
  return dbPromise;
}

export async function getCachedKlines(code: string, interval = '1d'): Promise<KlineRecord | null> {
  const db = await getDB();
  const rec = await db.get('klines', [code, interval]);
  return (rec as KlineRecord) ?? null;
}

export async function setCachedKlines(
  code: string,
  candles: Candle[],
  interval = '1d',
): Promise<void> {
  const db = await getDB();
  await db.put('klines', {
    code,
    interval,
    candles,
    lastFetchAt: Date.now(),
  });
}

/** 合并增量：旧 + 新（按 time 去重 + 排序） */
export function mergeCandles(old: Candle[], fresh: Candle[]): Candle[] {
  const map = new Map<number, Candle>();
  for (const c of old) map.set(c.time, c);
  for (const c of fresh) map.set(c.time, c);
  return Array.from(map.values()).sort((a, b) => a.time - b.time);
}

/** 是否需要刷新（>30 分钟） */
export function shouldRefresh(lastFetchAt: number, ttlMs = 30 * 60 * 1000): boolean {
  return Date.now() - lastFetchAt > ttlMs;
}

export async function getMeta(key: string): Promise<any> {
  const db = await getDB();
  const rec = (await db.get('meta', key)) as MetaRecord | undefined;
  return rec?.value;
}

export async function setMeta(key: string, value: any): Promise<void> {
  const db = await getDB();
  await db.put('meta', { key, value });
}

export async function clearAllKlines(): Promise<void> {
  const db = await getDB();
  await db.clear('klines');
}